import { TransactionDetails } from "../../../models/transaction";
import { Requestor } from "../../../models/requestor";
import { PatientMatchApiPayload, RequestorMatchApiPayload } from "../matchState";
import FormConstants, { RequestorFormConstants } from "./DataEntryFieldsConstants";

/**
 * Quick Entry REOPEN rehydration (AB#31605, direct user report).
 *
 * Root cause: Quick Entry's request-info rows populated ONLY from the AI
 * extraction (seedExtractedQuickEntryFields, gated on requestAnalysis) and
 * NEVER from the persisted transactionDetails. Confirm All DID persist
 * Clinic / Request Date / Case / Auth Date / Type-Purpose / the patient +
 * requestor links, but on a plain reopen (no fresh extraction) Quick Entry
 * didn't read them back, so those rows blanked and the patient/requestor rows
 * re-ran a stateless search. These pure mappers rebuild what Quick Entry needs
 * to fall back to the SAVED transaction, mirroring Classic Mode's
 * transactionDetails -> DATA_ENTRY_FIELDS_FORM initializer
 * (DataEntryDetailsRightPane) so the two modes can never drift.
 *
 * Precedence enforced by the callers: user's current edit > fresh AI extraction
 * > saved. These helpers only ever produce the SAVED fallback; the caller's
 * write-once falsy guard and doc-replace guard keep it below the other two.
 *
 * No PHI is logged here (values are returned to the caller, never logged) - see
 * .claude/rules/hipaa-compliance.md.
 */

/** A single [formField, value] pair to write into DATA_ENTRY_FIELDS_FORM. */
export type RehydrationPair = readonly [string, string | number | boolean];

/**
 * The Quick Entry request-info fields that defer to Confirm All - the SINGLE
 * source of truth for both directions of the precedence contract:
 *  - computeQuickEntryRehydration only ever fills fields in this set (reopen ->
 *    saved), bar the single RECEIVED_DATE exception marked in the list, and
 *  - clearQuickEntryDraftFields MUST blank every field in this set on a fresh
 *    extraction (new doc / re-run intake -> discard AI-seeded, manually entered,
 *    AND rehydrated-from-saved, so only the new extraction repopulates).
 * Keeping both consumers on this one list means the clear list can't silently
 * drift out of sync with what rehydration/seed can populate (AB#31605).
 */
export const QUICK_ENTRY_REQUEST_INFO_FIELDS: readonly string[] = [
    FormConstants.REQUEST_TYPE,
    FormConstants.PURPOSE_TYPE,
    FormConstants.CLINIC_RESPONISBLE,
    FormConstants.CASE_NUMBER,
    FormConstants.REQUEST_DATE,
    FormConstants.AUTHORIZATION_DATE,
    // AB#38061: RECEIVED_DATE is rehydrated by the mapper below but is deliberately NOT in this
    // list - the ONE exception to the contract above, unchanged from before this fix.
    //
    // CORRECTED (David Heifetz, PR !9998). This comment used to justify the exception by saying a
    // clear would leave the row "with nothing able to refill it: rehydration is armed once per
    // TRANSACTION, so it does not re-run on a replace". Only the second clause is true.
    // `quickEntryRehydratedTransactionId` is indeed not reset on a replace (DataEntryDetails.tsx
    // ~2311) - but the AI SEED is: `seededQeFields = new Set()` on a changed requestAnalysis
    // (DataEntryDetails.tsx:2335, and the latch's own docblock at :1983 says so). So when the
    // replacing document's extraction carries a receivedDate, the seed is exactly what WOULD refill
    // the row. The old wording asserted the opposite and would have misled the next maintainer.
    //
    // The real reason it stays out of the list: clearing it blanks the one REQUIRED request-info row,
    // and the seed can only refill it when the replacing extraction actually supplies a received
    // date - so on a replace whose extraction has none, a clear would leave the transaction blocked
    // on a value it already held.
    //
    // The residual gap, stated rather than implied: because seed()'s guard is `!values[field]`
    // (:2992) and it adds to the latch regardless (:2991), a value already sitting in the field
    // suppresses the replacing document's received date and never retries - so every other row shows
    // the new document's values while this one keeps the old, with no mismatch indicator. That is
    // PRE-EXISTING (it already fired for any non-empty value, from a prior extraction or a manual
    // entry) and DUE_DATE has the identical gap, also absent from this list and also seeded at
    // :3003. What this fix newly widens is narrow: reopen with a persisted DateArrived AND an
    // otherwise-empty field, then replace. Handling RECEIVED_DATE and DUE_DATE in the clear list is
    // the follow-up, and it is a change to the clear contract rather than to this fix.
    // DDS (Disability/Social Security) barcode fields (Spec #35136 / F1f) - added
    // so a document replace / fresh extraction clears them alongside the other
    // request-info fields (clearQuickEntryDraftFields), and so the reopen
    // rehydration path below can refill them, both from this one list.
    RequestorFormConstants.RQID,
    RequestorFormConstants.TX_SITE_CODE,
    RequestorFormConstants.RQ_SITE_CODE,
    RequestorFormConstants.DOC_RETURN_CODE,
    RequestorFormConstants.SSN,
    RequestorFormConstants.DOC_TYPE,
    RequestorFormConstants.ROUTING_FIELD,
    RequestorFormConstants.NO_BARCODE,
];

/**
 * Whether transactionDetails actually belongs to `transactionId` (AB#31605 review
 * MAJOR-1). transactionDetails is not cleared on an in-place transaction switch,
 * so during a switch X->Y props.transactionDetails still holds X's data until Y's
 * fetch resolves. The getTransactionAttributes saga builds the id as
 * `${facility.id}-${id}`, so comparing that to the current transactionId tells us
 * whether the details are current. Callers that write the shared form or the
 * persisted-link pre-resolve must gate on this so they can't act on another
 * transaction's data.
 */
export function transactionDetailsMatchesId(
    transactionDetails: TransactionDetails | undefined,
    transactionId: string | undefined,
): boolean {
    if (!transactionDetails || !transactionId) {
        return false;
    }
    return `${transactionDetails.facility?.id}-${transactionDetails.id}` === transactionId;
}

/** Inputs to the reopen persisted-match gate decision (all derived from props). */
export interface PersistedMatchGateInput {
    /** A fresh AI extraction happened this session (new doc / re-run intake). */
    readonly freshExtraction: boolean;
    /** The current transaction carries a persisted patient/requestor link id. */
    readonly hasLink: boolean;
    /** transactionDetails has loaded for the CURRENT transaction (so the link status is known). */
    readonly linkStatusKnown: boolean;
    /** The full persisted detail record has resolved (buildPersistedX returned a payload). */
    readonly persistedMatchResolved: boolean;
    /** The transaction's initial fetches (incl. the patient/requestor detail fetch) have settled. */
    readonly loadSettled: boolean;
}

/**
 * Decides how a Patient/Requestor row should treat the persisted link on reopen
 * (AB#31605 review MINOR-A race + MINOR-B wedge). Pure so the branching is
 * unit-testable without mounting the row. Returns two flags the row consumes:
 *  - `prefer`  -> pre-resolve from persistedMatch AND suppress the automated search.
 *  - `awaiting`-> HOLD the automated search and show a transient "matching" state
 *    because the link status / detail record is still pending.
 *
 * Contract:
 *  - Fresh extraction wins outright: never prefer/await a (now superseded) saved
 *    link - search the new keys.
 *  - RACE fix: while the link status isn't known yet (transactionDetails not
 *    loaded - e.g. the stale re-fetched requestAnalysis landed first), HOLD the
 *    search so it can't fire off stale keys before we know there's a saved link.
 *  - Still-loading (link present, record not yet resolved, load not settled) ->
 *    prefer + await (hold, show matching) until the record resolves.
 *  - WEDGE fix: once the load has SETTLED with no resolved record, STOP holding
 *    (prefer=false, awaiting=false) so the row degrades to its normal
 *    search/no-match path instead of spinning on "matching" forever.
 */
export function resolvePersistedMatchGate(input: PersistedMatchGateInput): { prefer: boolean; awaiting: boolean } {
    if (input.freshExtraction) {
        return { prefer: false, awaiting: false };
    }
    const prefer = input.hasLink && (input.persistedMatchResolved || !input.loadSettled);
    const awaiting = !input.persistedMatchResolved
        && (!input.linkStatusKnown || (input.hasLink && !input.loadSettled));
    return { prefer, awaiting };
}

/** A real saved value is anything other than empty-string / null / undefined / the 0 "- Select -" dropdown sentinel. */
function isRealSavedValue(value: unknown): boolean {
    return value !== undefined && value !== null && value !== "" && value !== 0;
}

/**
 * The seven request-info fields Quick Entry defers to Confirm All, mapped from the
 * persisted transactionDetails EXACTLY as Classic Mode's initializer maps them:
 * Request Type = requestPurpose.typeID, Purpose = requestPurpose.id, Clinic =
 * disclosingInstitution.id (as a string), Case Number as-is, and the three dates
 * from transactionDetails.date (Request, Authorization, and - AB#38061 - Received).
 * Only a field that (a) carries a real saved value
 * AND (b) is still EMPTY in the current form is returned - so a fresh AI seed or
 * a specialist edit already in the form is never overwritten (same falsy
 * write-once guard seedExtractedQuickEntryFields uses).
 */
export function computeQuickEntryRehydration(
    transactionDetails: TransactionDetails,
    formValues: Record<string, any>,
): RehydrationPair[] {
    const pairs: RehydrationPair[] = [];
    const add = (field: string, saved: string | number | undefined | null) => {
        if (!isRealSavedValue(saved)) {
            return;
        }
        // Falsy write-once guard: only fill an EMPTY form field.
        if (formValues[field]) {
            return;
        }
        pairs.push([field, saved as string | number]);
    };
    add(FormConstants.REQUEST_TYPE, transactionDetails.requestPurpose?.typeID);
    add(FormConstants.PURPOSE_TYPE, transactionDetails.requestPurpose?.id);
    add(FormConstants.CLINIC_RESPONISBLE, transactionDetails.disclosingInstitution?.id?.toString());
    add(FormConstants.CASE_NUMBER, transactionDetails.caseNumber);
    add(FormConstants.REQUEST_DATE, transactionDetails.date?.request);
    add(FormConstants.AUTHORIZATION_DATE, transactionDetails.date?.authorization);
    // AB#38061: read back the third date the same way. Quick Entry's step panes replace the main
    // DataEntryDetailsRightPane, so nothing carried date.received (tblRequestBuffer.DateArrived)
    // into the form on a reopen and the one REQUIRED request-info row read as NeedsEntry until the
    // specialist re-keyed a date the transaction already held. Written unconditionally like the two
    // siblings: a stored date the row's own rules reject is shown with its validation message, not
    // silently dropped.
    add(FormConstants.RECEIVED_DATE, transactionDetails.date?.received);

    // DDS fields (Spec #35136 / F1f, FR-8) - mirror Classic's own transactionDds
    // initializer (DataEntryDetailsRightPane.tsx:2694-2703). RQ_SITE_CODE is
    // seeded from the SAME txSiteCode source as TX_SITE_CODE (line 2696), matching
    // Classic. SSN rehydrates to its 3-star display mask (Classic's load-time
    // masking) - NEVER the raw/encrypted persisted value.
    const dds = transactionDetails.transactionDds;
    if (dds) {
        add(RequestorFormConstants.RQID, dds.rqid);
        add(RequestorFormConstants.TX_SITE_CODE, dds.txSiteCode);
        add(RequestorFormConstants.RQ_SITE_CODE, dds.txSiteCode);
        add(RequestorFormConstants.DOC_RETURN_CODE, dds.docReturnCode);
        add(RequestorFormConstants.DOC_TYPE, dds.docType);
        add(RequestorFormConstants.ROUTING_FIELD, dds.routingField);
        add(RequestorFormConstants.SSN, dds.ssn ? '***-**-***' : undefined);
        // No Barcode is a boolean the write-once/real-value guards would drop as
        // "falsy": handle it explicitly so a persisted true/false both rehydrate
        // into an EMPTY form field.
        if (dds.noBarcode !== undefined && dds.noBarcode !== null && !formValues[RequestorFormConstants.NO_BARCODE]) {
            pairs.push([RequestorFormConstants.NO_BARCODE, dds.noBarcode]);
        }
    }
    return pairs;
}

/** EMPI can arrive as a number or a numeric string; coerce to a clean number or undefined (never NaN). */
function toEmpiNumber(value: unknown): number | undefined {
    if (value == null || value === "") {
        return undefined;
    }
    const n = Number(value);
    return Number.isNaN(n) ? undefined : n;
}

/** True once leftPanePatientDetailsAttributes has actually been fetched (it defaults to {} - an empty object is "not loaded yet"). */
function isPatientRecordLoaded(patientDetails: any): boolean {
    if (!patientDetails) {
        return false;
    }
    const contact = patientDetails.contact;
    return Boolean(
        contact?.firstName || contact?.lastName || patientDetails.dob || patientDetails.mrn || patientDetails.empi,
    );
}

/**
 * Rebuilds a PatientInfoRow pre-resolved "matched single" apiResult from the
 * transaction's persisted patient link (transactionDetails.patient.id) plus the
 * already-loaded patient record (leftPanePatientDetailsAttributes, auto-fetched
 * by the getTransactionAttributes saga on reopen). candidates[0].patientId
 * carries the persisted id so the row's existing onSelectPatient write-once
 * effect re-links it to the transaction on a re-save, exactly as an automated
 * match would.
 *
 * STRICT / FULL-ONLY (AB#31605 review MAJOR-2): returns undefined unless BOTH
 * (a) the transaction carries a linked patient AND (b) that patient's detail
 * record has actually loaded. This is deliberate - the row pre-resolves ONCE and
 * the onSelectPatient write-once effect writes the form ONCE, so pre-resolving on
 * a half-loaded (name-only) record would freeze blank DOB/MRN into the form. The
 * caller shows a transient "matching" state until this returns a full payload.
 * The parent passes transactionDetails only when it belongs to the CURRENT
 * transaction (identity guard), so this never builds from another tx's data.
 */
export function buildPersistedPatientMatch(
    transactionDetails: TransactionDetails | undefined,
    patientDetails: any,
): PatientMatchApiPayload | undefined {
    const patientId = transactionDetails?.patient?.id;
    if (!patientId || !isPatientRecordLoaded(patientDetails)) {
        return undefined;
    }
    const contact = patientDetails.contact ?? {};
    const firstName = contact.firstName ?? undefined;
    const lastName = contact.lastName || transactionDetails?.patient?.name || undefined;
    const middleName = contact.middleInitial || contact.middleName || undefined;
    const dob = patientDetails.dob ?? undefined;
    const mrn = patientDetails.mrn || transactionDetails?.mrn || undefined;
    const empi = toEmpiNumber(patientDetails.empi);
    return {
        matched: true,
        matchMethod: "LastNameDob",
        matchCount: 1,
        confidence: "Single",
        patient: { contact: { firstName, middleName, lastName }, dob, mrn, empi },
        candidates: [{ patientId, firstName, lastName, dob, mrn, empi }],
    };
}

/**
 * Rebuilds a RequestorInfoRow pre-resolved "matched single" apiResult from the
 * transaction's persisted requestor link (sendToRequestor.id + sendToContact.id)
 * plus the already-loaded requestor record (getRequestorDetailsSuccess, also
 * auto-fetched by the getTransactionAttributes saga on reopen). requestorId /
 * contactId carry the persisted ids so the row's existing onSelectRequestor
 * write-once effect re-links them on a re-save.
 *
 * STRICT / FULL-ONLY + IDENTITY-CHECKED (AB#31605 review MAJOR-2):
 *  - The link is ONLY transactionDetails.sendToRequestor.id - the old
 *    `?? requestorDetails.requestorID` fallback is dropped, since requestorDetails
 *    is redux state that is not cleared on a transaction switch and could
 *    fabricate a match for a requestor the current tx has no link to.
 *  - The full detail (address/contact/fax/etc.) is taken from requestorDetails
 *    ONLY when its requestorID matches the linked id - so a stale prior-tx record
 *    is never used, and (because getRequestorDetailsSuccess lands AFTER
 *    getTransactionDetailsSuccess) the row does not pre-resolve name-only and
 *    freeze a blank address/contact into the send-to form (the AB#35081 symptom).
 *    Until the matching record loads this returns undefined and the caller shows a
 *    transient "matching" state.
 *
 * Limitation: match-metadata that only exists on a live search result - the
 * "matched on fax/email" method, the National flag, and the prior-request count
 * - is NOT stored on the requestor record, so a reopened requestor shows the
 * generic match note and no prior-count. eTransfer is derived from the record's
 * eTransferSetup, matching Classic's own `!!requestorDetails.eTransferSetup`
 * check, so the E-Transfer badge (AB#35081) still shows on reopen.
 */
export function buildPersistedRequestorMatch(
    transactionDetails: TransactionDetails | undefined,
    requestorDetails: Requestor | undefined,
): RequestorMatchApiPayload | undefined {
    const requestorId = transactionDetails?.sendToRequestor?.id;
    // Only trust the detail record when it belongs to THIS linked requestor.
    if (!requestorId || requestorDetails?.requestorID !== requestorId) {
        return undefined;
    }
    const info = requestorDetails.requestorInfo;
    const addr = requestorDetails.requestorAddress;
    return {
        matched: true,
        requestorId,
        fullName: requestorDetails.fullName || transactionDetails?.sendToRequestor?.name || undefined,
        address1: addr?.address1 || undefined,
        address2: addr?.address2 || undefined,
        city: addr?.city || undefined,
        state: addr?.state || undefined,
        zipCode: addr?.zipCode || undefined,
        eTransfer: !!requestorDetails.eTransferSetup,
        national: false,
        transactionCount: 0,
        contactId: transactionDetails?.sendToContact?.id ?? info?.id,
        contactFirstName: info?.firstName ?? undefined,
        contactLastName: info?.lastName ?? undefined,
        email: info?.email ?? undefined,
        phone: info?.phone ?? undefined,
        fax: info?.fax ?? undefined,
        matchMethod: "None",
        matchCount: 1,
        confidence: "Single",
    };
}
